/**
 * Mixpanel Detection Utility
 * 
 * Provides adaptive polling mechanism to detect when Mixpanel is loaded.
 * Uses multiple strategies:
 * 1. Event listener for MixpanelReady event
 * 2. Adaptive polling with backoff strategy
 * 3. Timeout fallback after ~2.5 seconds
 */

/**
 * Detects when Mixpanel is ready using adaptive polling and event listening
 * 
 * @param {Function} onReady - Callback when Mixpanel is detected or timeout reached
 * @returns {Object} Object with cleanup function
 */
export const detectMixpanel = (onReady) => {
  let initialized = false;
  let attempts = 0;
  let cleanupFns = [];

  const executeCallback = () => {
    if (!initialized) {
      initialized = true;
      onReady();
    }
  };

  // Check if already loaded
  if (window.mixpanel && window.mixpanel.__loaded) {
    executeCallback();
    return { cleanup: () => {} };
  }

  // Mechanism 1: Listen for MixpanelReady event
  const onMixpanelReady = () => executeCallback();
  window.addEventListener('MixpanelReady', onMixpanelReady);
  cleanupFns.push(() => window.removeEventListener('MixpanelReady', onMixpanelReady));

  // Mechanism 2: Adaptive Polling
  // Start aggressive, then back off if Mixpanel isn't loading
  const intervals = [
    50, 50, 50, 50,      // First 4 checks: 50ms each (0-200ms)
    100, 100, 100, 100,  // Next 4 checks: 100ms each (200-600ms)
    200, 200, 200,       // Next 3 checks: 200ms each (600-1200ms)
    500, 500             // Last 2 checks: 500ms each (1200-2200ms)
  ]; // Total: ~2.2 seconds

  let timeoutId = null;

  const scheduleNextCheck = () => {
    if (initialized) return;

    if (attempts >= intervals.length) {
      // Max attempts reached - proceed without Mixpanel
      console.warn('[Mixpanel] Max detection attempts reached. Proceeding without Mixpanel after ~2.2s');
      executeCallback();
      return;
    }

    const delay = intervals[attempts];
    attempts++;

    timeoutId = setTimeout(() => {
      if (initialized) return;

      if (window.mixpanel && window.mixpanel.__loaded) {
        executeCallback();
      } else {
        scheduleNextCheck();
      }
    }, delay);
  };

  scheduleNextCheck();
  cleanupFns.push(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });

  // Return cleanup function
  return {
    cleanup: () => {
      cleanupFns.forEach(fn => fn());
      cleanupFns = [];
    }
  };
};

/**
 * Get Mixpanel distinct ID if available
 * 
 * @returns {string|null} Mixpanel distinct ID or null if not available
 */
export const getMixpanelId = () => {
  if (window.mixpanel && window.mixpanel.get_distinct_id) {
    return window.mixpanel.get_distinct_id();
  }
  return null;
};
